import { Link } from "react-router-dom";
import { Settings as SettingsIcon, Lock, Shield, LifeBuoy, BookOpen } from "lucide-react";
import { NAV } from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { useHousehold } from "@/contexts/HouseholdContext";
import { hasProAccess } from "@/lib/plans";

// Mobile-only "app launcher" page — every sidebar section shown as a colorful
// tappable card. Reachable via the profile dropdown's "Home" shortcut (lg:hidden),
// since the full sidebar is already visible on desktop and this page adds nothing there.
const SETTINGS_COLOR = "#64748B";
const CONTACT_COLOR = "#0891B2";
const HELP_COLOR = "#6366F1";
const ADMIN_COLOR = "#E11D48";

export default function MobileMenu() {
    const { user } = useAuth();
    const { isMember } = useHousehold();
    const hasPro = hasProAccess(user, isMember);
    const isAdmin = user?.role === "admin";
    return (
        <div className="space-y-4 lg:hidden" data-testid="mobile-menu-page">
            <div className="grid grid-cols-2 gap-3">
                {NAV.map(({ to, label, icon: Icon, testid, color, pro }) => (
                    <Link
                        key={to} to={to} data-testid={`menu-card-${testid}`}
                        className="relative card-elev rounded-2xl p-4 flex flex-col items-start gap-3 active:scale-[0.97] transition-transform"
                        style={{ backgroundColor: `${color}0F` }}
                    >
                        {pro && !hasPro && <Lock className="w-3.5 h-3.5 absolute top-3.5 right-3.5 text-slate-400" />}
                        <span className="w-11 h-11 rounded-xl grid place-items-center" style={{ backgroundColor: `${color}22`, color }}>
                            <Icon className="w-5 h-5" />
                        </span>
                        <span className="font-semibold text-sm text-slate-900">{label}</span>
                    </Link>
                ))}
                <Link
                    to="/settings" data-testid="menu-card-nav-settings"
                    className="card-elev rounded-2xl p-4 flex flex-col items-start gap-3 active:scale-[0.97] transition-transform"
                    style={{ backgroundColor: `${SETTINGS_COLOR}0F` }}
                >
                    <span className="w-11 h-11 rounded-xl grid place-items-center" style={{ backgroundColor: `${SETTINGS_COLOR}22`, color: SETTINGS_COLOR }}>
                        <SettingsIcon className="w-5 h-5" />
                    </span>
                    <span className="font-semibold text-sm text-slate-900">Settings</span>
                </Link>
                <Link
                    to="/help" data-testid="menu-card-nav-help"
                    className="card-elev rounded-2xl p-4 flex flex-col items-start gap-3 active:scale-[0.97] transition-transform"
                    style={{ backgroundColor: `${HELP_COLOR}0F` }}
                >
                    <span className="w-11 h-11 rounded-xl grid place-items-center" style={{ backgroundColor: `${HELP_COLOR}22`, color: HELP_COLOR }}>
                        <BookOpen className="w-5 h-5" />
                    </span>
                    <span className="font-semibold text-sm text-slate-900">Help &amp; tips</span>
                </Link>
                <Link
                    to="/contact" data-testid="menu-card-nav-contact"
                    className="card-elev rounded-2xl p-4 flex flex-col items-start gap-3 active:scale-[0.97] transition-transform"
                    style={{ backgroundColor: `${CONTACT_COLOR}0F` }}
                >
                    <span className="w-11 h-11 rounded-xl grid place-items-center" style={{ backgroundColor: `${CONTACT_COLOR}22`, color: CONTACT_COLOR }}>
                        <LifeBuoy className="w-5 h-5" />
                    </span>
                    <span className="font-semibold text-sm text-slate-900">Contact Us</span>
                </Link>
                {isAdmin && (
                    <Link
                        to="/admin" data-testid="menu-card-nav-admin"
                        className="card-elev rounded-2xl p-4 flex flex-col items-start gap-3 active:scale-[0.97] transition-transform"
                        style={{ backgroundColor: `${ADMIN_COLOR}0F` }}
                    >
                        <span className="w-11 h-11 rounded-xl grid place-items-center" style={{ backgroundColor: `${ADMIN_COLOR}22`, color: ADMIN_COLOR }}>
                            <Shield className="w-5 h-5" />
                        </span>
                        <span className="font-semibold text-sm text-slate-900">Admin</span>
                    </Link>
                )}
            </div>
        </div>
    );
}
